import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import { PAYROLL_VOUCHER_LABELS } from "../messages";

export const EXPORT_ACKNOWLEDGEMENT_TEST_ID: string = "export-acknowledgement";

const ACKNOWLEDGEMENT_FIELD_ID: string = "export-acknowledgement-field";

interface FiscalCycleExportModalProps {
  cycle: string;
  onConfirm: () => Promise<void>;
}

export function FiscalCycleExportModal({
  cycle,
  onConfirm,
}: FiscalCycleExportModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const closeModal = (): void => {
    handleModal({ isOpen: false });
  };

  const confirm = async (): Promise<void> => {
    if (!acknowledged || isSubmitting) return;

    setIsSubmitting(true);

    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center space-y-6">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
        <AlertTriangle className="h-8 w-8 text-amber-600" />
      </div>

      <p className="text-center text-sm font-medium text-gray-900">
        {PAYROLL_VOUCHER_LABELS.EXPORT_CYCLE_TARGET(cycle)}
      </p>

      <p className="text-center text-sm text-gray-700">
        {PAYROLL_VOUCHER_LABELS.EXPORT_CLOSES_PERIOD}
      </p>

      <p className="text-center text-xs text-gray-500">
        {PAYROLL_VOUCHER_LABELS.EXPORT_IS_INCREMENTAL}
      </p>

      <label
        htmlFor={ACKNOWLEDGEMENT_FIELD_ID}
        className="flex w-full cursor-pointer items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-3"
      >
        <Checkbox
          id={ACKNOWLEDGEMENT_FIELD_ID}
          data-testid={EXPORT_ACKNOWLEDGEMENT_TEST_ID}
          checked={acknowledged}
          disabled={isSubmitting}
          onCheckedChange={(value) => setAcknowledged(!!value)}
          className="mt-0.5"
        />
        <span className="text-sm text-amber-900">
          {PAYROLL_VOUCHER_LABELS.EXPORT_ACKNOWLEDGEMENT}
        </span>
      </label>

      <div className="flex w-full space-x-3">
        <Button
          variant="outline"
          onClick={closeModal}
          disabled={isSubmitting}
          className="flex-1"
        >
          {PAYROLL_VOUCHER_LABELS.CANCEL}
        </Button>
        <Button
          onClick={confirm}
          disabled={!acknowledged || isSubmitting}
          className="flex-1"
        >
          {isSubmitting
            ? PAYROLL_VOUCHER_LABELS.PROCESSING
            : PAYROLL_VOUCHER_LABELS.EXPORT_CONFIRM}
        </Button>
      </div>
    </div>
  );
}
